import React, { useEffect, useRef, useState } from 'react';
import { animated } from 'react-spring';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { SET_RESTART_OFF, SET_RESTART_ON, OVER_RESTART } from '../../HOME/REDUX/actions';
import { FORCE_UPDATE, REQUEST_RESET_STATS_OFF, REQUEST_SURRENDER_OFF, SHOW_LEADERS } from '../../HOME/REDUX/actions';
import { delay, playSound, windowSize, dbget, dbdel, dbset } from '../../HOME/utils';
import * as f from '../logic';
import { PAUSE_ON, PAUSE_OFF, SET_OVER_OFF, SET_OVER_ON } from '../REDUX/actions';
import { UPDATE_STATS, SET_CLEARED, SET_BONUS, SET_SECS } from '../REDUX/actions';
import '../styles.css';
import MessageBox from '../../HOME/COMPONENTS/MessageBox';
import { ROW_COUNT, COL_COUNT } from '../const';
import Cell from './Cell';
import { useInterval } from '../../HOME/utils';
import ResumeIcon from '../../HOME/SVG/PlayIcon';
import GameOverOptions from '../../HOME/COMPONENTS/GameOverOptions';

const Board = () => {
  const l = useRef({ red: 0, yellow: 0, blue: 0, assigned: 0 });
  const restarting = useRef(false);
  const { cm, common } = useSelector(state => state);
  const { size, paused } = cm;
  const { restart } = common;
  const [paletteKey, setPaletteKey] = useState(0);
  const [cells, setCells] = useState([...Array(42)].map((_, i) => ({ index: i })));
  const dispatch = useDispatch();

  const forceUpdate = () => dispatch({ type: FORCE_UPDATE });

  useEffect(() => {
    const onResize = () => {
      forceUpdate();
    };

    if (l.current.started && !restart) {
      return;
    }

    window.addEventListener('resize', onResize);

    if (restart) {
      doRestart();
    } else {
      load();
    }

    dispatch(SET_RESTART_OFF);
    restarting.current = false;

    return () => {
      window.removeEventListener('resize', onResize);
      dispatch(REQUEST_RESET_STATS_OFF);
      dispatch(REQUEST_SURRENDER_OFF);
      dispatch(SET_OVER_OFF);

      if (!restart && !restarting.current) {
        dispatch(PAUSE_ON);
      }
    };
  }, [restart]);

  const doRestart = () => {
    dbdel(f.boardKey(size));

    if (size) {
      setTime(Math.max(l.current.secs, 10 * size));
    }

    setStats();
    doInit();
  };

  const load = () => {
    dbget(f.boardKey(size), json => {
      if (json) {
        doLoad(json);
      } else {
        doInit();
      }
    });
  };

  const doLoad = json => {
    const { secs, cleared, bonus, assigned, speed, cells } = fromJson(json);

    dispatch({ type: SET_CLEARED, count: (l.current.cleared = cleared) });
    dispatch({ type: SET_BONUS, count: (l.current.bonus = bonus) });

    l.current.assigned = assigned;
    l.current.speed = speed;

    _.each(cells, (cell, i) => (cell.index = i));
    setCells(cells);

    setTime(secs);

    dispatch(PAUSE_ON);
    l.current.started = true;

    forceUpdate();
  };

  const doInit = () => {
    playSound(common, 'dice');

    dispatch(PAUSE_OFF);
    dispatch(SET_OVER_OFF);

    setPaletteKey((l.current.red = l.current.yellow = l.current.blue = 0));
    dispatch({ type: SET_CLEARED, count: (l.current.cleared = 0) });
    dispatch({ type: SET_BONUS, count: (l.current.bonus = 0) });

    const assign = i => {
      assignKey(cells[i], Number(_.sample(f.paletteKeys())));
    };

    if (size) {
      setTime(0);
      l.current.assigned = 0;
    } else {
      l.current.speed = 0;
    }

    const indexes = _.map(_.sampleSize(_.keys(cells), size ? Math.min(size, ROW_COUNT * COL_COUNT - 1) : 10), i => Number(i));

    _.each(cells, cell => (cell.key = 0));
    _.each(indexes, i => assign(i));

    l.current.started = true;
    forceUpdate();
  };

  const onTick = () => {
    if (size) {
      const secs = (cm.secs || 0) + 1;

      if (secs > 10 * size) {
        return;
      }

      setTime(secs);
    } else {
      addCell();

      l.current.speed += 2;

      if (starred().length === ROW_COUNT * COL_COUNT) {
        onOver();
      }
    }

    save();
  };

  const setTime = secs => {
    dispatch({ type: SET_SECS, secs: (l.current.secs = secs) });
  };

  useInterval(onTick, paused || restarting.current ? null : size ? 1000 : 1600 - (l.current.speed || 0));

  const starred = () => {
    return _.filter(cells, cell => cell.key);
  };

  const unstarred = () => {
    return _.filter(cells, cell => !cell.key);
  };

  const assignKey = (cell, key) => {
    if (size) {
      if (l.current.cleared + starred().length < size && l.current.assigned < size) {
        cell.key = key;
        l.current.assigned += 1;
      }
    } else {
      cell.key = key;
    }
  };

  const hideCell = cell => {
    cell.key = 0;
    dispatch({ type: SET_CLEARED, count: ++l.current.cleared });

    if (size) {
      if (l.current.cleared === size) {
        onOver();
        return;
      }

      if (l.current.assigned < size && unstarred().length > 10) {
        delay(addCell, 1000);
      }
    } else {
      if (!starred().length) {
        playSound(common, 'coins');
        dispatch({ type: SET_BONUS, count: (l.current.bonus += 10) });
      }
    }
  };

  const onOver = () => {
    playSound(common, 'won');
    dispatch(SET_OVER_ON);
    l.current.started = false;
    dispatch(PAUSE_ON);
    Promise.resolve().then(setStats);
  };

  const setStats = () => {
    dbdel(f.boardKey(size));

    let { plays, aggSecs, bestSecs, aggScore, bestScore } = cm.stats || {};
    plays = (plays || 0) + 1;

    let stats;

    if (size) {
      const secs = l.current.secs;
      aggSecs = (aggSecs || 0) + secs;
      const aveSecs = _.round(aggSecs / plays);
      bestSecs = bestSecs ? Math.min(secs, bestSecs) : secs;

      stats = { plays, aggSecs, aveSecs, bestSecs };
    } else {
      const score = l.current.cleared + l.current.bonus;
      aggScore = (aggScore || 0) + score;
      const aveScore = _.round(aggScore / plays);
      bestScore = bestScore ? Math.max(score, bestScore) : score;

      stats = { plays, aveScore, aggScore, bestScore };
    }

    dbset(f.statsKey(size), stats);
    dispatch({ type: UPDATE_STATS, stats });
  };

  const addCell = () => {
    const indexes = _.map(unstarred(), cell => cell.index);
    const i = _.sample(indexes);

    do {
      var key = Number(_.sample(f.paletteKeys()));
    } while (key === paletteKey);

    assignKey(cells[i], key);
    forceUpdate();
  };

  const save = () => {
    dbset(f.boardKey(size), toJson());
  };

  const toJson = secs => {
    const json = JSON.stringify(
      { secs: l.current.secs, cleared: l.current.cleared, bonus: l.current.bonus, assigned: l.current.assigned, speed: l.current.speed, cells },
      ['secs', 'cleared', 'bonus', 'assigned', 'speed', 'cells', 'key']
    );

    return json;
  };

  const fromJson = json => {
    return JSON.parse(json);
  };

  const handleTouch = i => {
    l.current.touch = true;
    handleClick(i);
  };

  const handleMouseDown = i => {
    if (!l.current.touch) {
      handleClick(i);
    }
  };

  const handleClick = i => {
    if (paused) {
      return;
    }

    const weight = i % 3;

    if (i < 3) {
      l.current.red = weight;
    } else if (i < 6) {
      l.current.yellow = weight;
    } else {
      l.current.blue = weight;
    }

    const key = l.current.red * 100 + l.current.yellow * 10 + l.current.blue;

    if (key === paletteKey) {
      return;
    }

    playSound(common, 'tap');

    setPaletteKey(key);

    clearTimeout(l.current.timeout);

    if (key) {
      l.current.timeout = delay(() => {
        clear(key);
      }, 500);
    }
  };

  const clear = key => {
    let update = 0;

    _.each(cells, (cell, i) => {
      if (cell.key !== key) {
        return;
      }

      const { row, col } = f.cellPos(i);
      let r, c;

      for (let j = 0; j < 4; j++) {
        r = row;
        c = col;

        switch (j) {
          case 0:
            r = row - 1;
            break;
          case 1:
            c = col + 1;
            break;
          case 2:
            r = row + 1;
            break;
          case 3:
            c = col - 1;
            break;
          default:
        }

        if (r < 1 || r > ROW_COUNT || c < 1 || c > COL_COUNT) {
          continue;
        }

        const k = cellIndex(r, c);

        if (!cells[k].key) {
          hideCell(cell);
          ++update;
          break;
        }
      }
    });

    if (update) {
      playSound(common, update === 1 ? 'coin1' : 'coin2');

      forceUpdate();

      if (_.find(cells, cell => cell.key === key)) {
        clear(key);
      }
    }
  };

  const getBackground = key => {
    return key ? f.palette[key].background : 'white';
  };

  const getForeground = key => {
    return key ? f.palette[key].foreground : 'black';
  };

  const cellIndex = (row, col) => {
    return (row - 1) * COL_COUNT + col - 1;
  };

  const isSelected = (row, col) => {
    const { red, yellow, blue } = l.current;

    switch (row) {
      case 0:
        return col === red;
      case 1:
        return col === yellow;
      case 2:
        return col === blue;
      default:
    }

    return false;
  };

  let { x, y } = windowSize();
  y -= 200;
  let width = Math.min(x, y) * 0.9;
  width = Math.min(width, 321);

  const handleConfirmSurrender = yes => {
    delay(() => {
      dispatch(REQUEST_SURRENDER_OFF);

      if (!yes) {
        return;
      }

      restarting.current = true;
      dispatch(SET_RESTART_ON);
    });
  };

  const handleConfirmResetStats = yes => {
    if (yes) {
      dispatch({ type: UPDATE_STATS });
      dbdel(f.statsKey(size));
    }

    delay(() => dispatch(REQUEST_RESET_STATS_OFF));
  };

  const handleResume = () => {
    dispatch(PAUSE_OFF);
  };

  const background = getBackground(paletteKey);

  const handleGameOver = op => {
    if (op === SHOW_LEADERS) {
      dispatch(PAUSE_ON);
      dispatch({ type: SHOW_LEADERS });
    } else if (op === OVER_RESTART) {
      doInit();
    }
  };

  return (
    <div className="cm-board-container">
      <div className="cm-board" style={{ height: `${(321 / COL_COUNT) * ROW_COUNT}px`, background }}>
        {paused && l.current.started ? (
          <animated.div className="ms-resume cm-resume fadein-fast" onClick={handleResume}>
            <>
              <span className="re-game-paused" style={{ color: 'darkgreen' }}>
                GAME PAUSED
              </span>
              <ResumeIcon width="100px" color="darkgreen" style={{ gridArea: '1/1' }} />
            </>
          </animated.div>
        ) : (
          _.map(cells, (cell, i) => {
            return _.isEmpty(cell) ? null : <Cell key={i} cell={cell} index={i} />;
          })
        )}
        {cm.over && !common.resetStats && (
          <GameOverOptions header="Game Over" handleResponse={handleGameOver} style={{ gridArea: '1/1 / span 7 / span 6' }} />
        )}
      </div>
      <div className="cm-color-picker">
        {_.map(['#FFF', '#FF8080', '#F00', '#FFF', '#FFFF80', '#FF0', '#FFF', '#95B0CC', '#2A6099'], (background, i) => {
          let row = Math.floor(i / 3);
          let col = i % 3;
          const selected = isSelected(row, col);

          row = (row + 1) * 2;
          col += 1;

          return (
            <div
              key={i}
              className="cm-picker-item"
              style={{ gridArea: `${row}/${col}`, background }}
              onTouchStart={() => handleTouch(i)}
              onMouseDown={() => handleMouseDown(i)}>
              {selected && <span className="cm-picker-bullet" />}
            </div>
          );
        })}
      </div>
      {common.surrender && <MessageBox message="Start over?" yesNo width={width} onReply={handleConfirmSurrender} />}
      {common.resetStats && <MessageBox message="Reset stats?" yesNo width={width} onReply={handleConfirmResetStats} />}
    </div>
  );
};

export default Board;
